/* ============================================================================
   Onboarding-stage status — shared helper for the Courier Setup module.

   A courier is "fully onboarded" once these stages are complete (v6 onboarding flow):
     1. Partner onboarded        — parent exists (implicit; the 360 page is open)
     2. Account code created      — at least one ACTIVE account code
     3. Onboarding checklist done — all checklist items met (POCs / KAM / COD SLA …)
     4. Agreement signed+approved — a signed (Stage-2) agreement is ACTIVE & governing
     5. Rate card uploaded        — priced rate card for the code(s)
     6. Zone mapping uploaded     — base READY and published for the code(s)

   Every derivation here mirrors the existing logic in Partner360's
   SetupStatusStrip / scoreComponents and the agreement health helpers, so the
   new section / per-code badges stay 100% consistent with what the page already
   shows. Pure functions + one tiny presentational pill — no data fetching.
   ========================================================================== */
import React from 'react'
import PropTypes from 'prop-types'
import {Chip} from '@mui/material'

/* three-state onboarding tone — Pending (not started) · In progress (partial) · Done */
export const STAGE_TONE = {
  done: {bg: '#DCFCE7', fg: '#166534', label: 'Done'},
  inprogress: {bg: '#DBEAFE', fg: '#1D4ED8', label: 'In progress'},
  pending: {bg: '#FEE2E2', fg: '#B91C1C', label: 'Pending'},
  na: {bg: '#F1F5F9', fg: '#475569', label: '—'},
}

/* small status pill used in the section nav + the codes table */
export function StatusPill({state, label, title, sx}) {
  const t = STAGE_TONE[state] || STAGE_TONE.na
  return (
    <Chip
      size='small'
      label={label || t.label}
      title={title || undefined}
      sx={{height: 19, fontSize: 10, fontWeight: 700, borderRadius: '6px', bgcolor: t.bg, color: t.fg, '& .MuiChip-label': {px: 0.75}, ...sx}}
    />
  )
}
StatusPill.propTypes = {
  state: PropTypes.string,
  label: PropTypes.string,
  title: PropTypes.string,
  sx: PropTypes.object,
}

/* aggregate tri-state across N items: all done · some done · none done */
export const triState = (doneN, total) => (total > 0 && doneN >= total ? 'done' : doneN > 0 ? 'inprogress' : 'pending')

/* ── per-stage derivations ─────────────────────────────────────────────── */

/* Stage 2 — account code(s). Done once at least one code is ACTIVE. */
export const codesState = (codes) => {
  const list = codes || []
  if (!list.length) return 'pending'
  return list.some((c) => c.status === 'Active' || c.isActive === true) ? 'done' : 'inprogress'
}

/* Stage 4 — agreement. Done once a signed (Stage-2) agreement is ACTIVE & governing.
   `timeline` items carry an `agreement` doc (stage/status). */
export const agreementState = (timeline) => {
  const items = timeline || []
  if (!items.length) return 'pending'
  const signedActive = items.some((t) => t.agreement && t.agreement.stage === 'SIGNED' && t.agreement.status === 'ACTIVE')
  return signedActive ? 'done' : 'inprogress'
}

/* Stage 3 — onboarding checklist. `comps` is scoreComponents() output ({met}). */
export const checklistState = (comps) => {
  const list = comps || []
  if (!list.length) return 'pending'
  return triState(list.filter((c) => c.met).length, list.length)
}

/* a rate-card object (parent/{id}/rateCards) has at least one priced slab */
export const cardHasRate = (c) =>
  !!(c && c.activeSla && c.activeSla.sla && Object.values(c.activeSla.sla).some((z) => (z.slab || []).some((s) => s.price != null)))

/* Stage 5 — rate cards. `cards` = parent rate-card list; total = number of codes. */
export const rateCardState = (cards, total) => triState((cards || []).filter(cardHasRate).length, total)

/* a normalised zone view (normView) is published — base READY and published at
   least once (matches SetupStatusStrip's pubN count + the "zone uploaded" stage). */
export const zoneViewDone = (view) => !!(view && view.base && view.base.status === 'READY' && view.lastPublishedAt)
/* base seeded (work started) even if not yet published */
export const zoneBaseReady = (view) => !!(view && view.base && view.base.status === 'READY')

/* Stage 6 — zone mapping. `views` = normView per code; total = number of codes. */
export const zoneState = (views, total) => {
  const list = views || []
  const baseReady = list.some(zoneBaseReady)
  if (!baseReady) return 'pending'
  const published = list.filter(zoneViewDone).length
  return published >= total && total > 0 ? 'done' : 'inprogress'
}

/* per-code label for what's still pending on a single code (codes table). */
export const pendingCodeItems = ({active, agreementDone, rateDone, zoneDone}) => {
  const out = []
  if (!active) out.push('Activate code')
  if (!agreementDone) out.push('Agreement')
  if (!rateDone) out.push('Rate card')
  if (!zoneDone) out.push('Zone mapping')
  return out
}
